import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { AuthSession, CurrentUser, Role } from 'app/auth/models';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //public
  public currentUser: Observable<CurrentUser>;

  //private
  private currentUserSubject: BehaviorSubject<CurrentUser>;

  /**
   *
   * @param {HttpClient} _http
   * @param {ToastrService} _toastrService
   */
  constructor(private _http: HttpClient, private _toastrService: ToastrService) {
    const currentUser = this.loadStoredUser();
    this.currentUserSubject = new BehaviorSubject<CurrentUser>(currentUser);
    this.currentUser = this.currentUserSubject.asObservable();
  }

  // getter: currentUserValue
  public get currentUserValue(): CurrentUser {
    return this.currentUserSubject.value;
  }

  public get authToken(): string {
    const auth = this.getStoredAuth();
    return auth && auth.token ? auth.token : null;
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    return this.currentUserSubject.value && this.currentUserSubject.value.role === Role.Admin;
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    return this.currentUserSubject.value && this.currentUserSubject.value.role === Role.Client;
  }

  /**
   * User login
   *
   * @param email
   * @param password
   * @returns user
   */
  login(username: string, password: string) {
    this.clearSession();
    this.currentUserSubject.next(null);

    return this._http
      .post<any>(`${environment.apiUrl}/Auth/login`, { username, password })
      .pipe(
        switchMap(response => {
          const token = response && response.data ? response.data.token : null;
          if (!response || !response.isSuccessful || !token) {
            this.clearSession();
            return throwError(this.getResponseError(response));
          }

          return this.fetchUser(token).pipe(
            map(user => {
              const currentUser = this.getNormalizedUserInfo(user);
              localStorage.setItem('Auth', JSON.stringify({ token }));
              localStorage.setItem('currentUser', JSON.stringify(currentUser));
              this.currentUserSubject.next(currentUser);
              return response;
            }),
            tap(() => {
              const currentUser = this.currentUserValue;
              setTimeout(() => {
                this._toastrService.success(
                  'You have successfully logged in as an ' +
                  currentUser.role +
                  ' user to W F W. Now you can start to explore.',
                  ' Welcome, ' + currentUser.firstName + '!',
                  { toastClass: 'toast ngx-toastr', closeButton: true }
                );
              }, 2500);
            })
          );
        })
      );
  }


  getUser(token: string) {
    return this.fetchUser(token).pipe(
      map(user => {
        const currentUser = this.getNormalizedUserInfo(user);
        localStorage.setItem('Auth', JSON.stringify({ token }));
        localStorage.setItem('currentUser', JSON.stringify(currentUser));
        this.currentUserSubject.next(currentUser);
        return currentUser;
      })
    );
  }
  /**
   * User logout
   *
   */
  logout() {
    this.clearSession();
    // notify
    this.currentUserSubject.next(null);
  }

  private fetchUser(token: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${token}`
    });

    return this._http.get<any>(`${environment.apiUrl}/User/info`, { headers });
  }

  private getResponseError(response: any): string {
    const messages = response && (response.message || response.messages);
    const firstMessage = Array.isArray(messages) ? messages[0] : null;

    return (firstMessage && (firstMessage.messageText || firstMessage.message)) || 'Unable to log in.';
  }

  private loadStoredUser(): CurrentUser {
    const auth = this.getStoredAuth();
    const currentUser = this.normalizeUser(this.getStoredCurrentUser());

    if (auth && auth.role != null) {
      if (auth.token && this.toRole(auth.role) === Role.Admin) {
        const legacyAdmin = this.normalizeUser(auth);
        localStorage.setItem('Auth', JSON.stringify({ token: auth.token }));
        localStorage.setItem('currentUser', JSON.stringify(legacyAdmin));
        return legacyAdmin;
      }

      this.clearSession();
      return null;
    }

    if (auth && auth.token && currentUser && currentUser.role === Role.Admin) {
      localStorage.setItem('Auth', JSON.stringify({ token: auth.token }));
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      return currentUser;
    }

    if (auth || currentUser) {
      this.clearSession();
    }

    return null;
  }

  private getNormalizedUserInfo(response: any): CurrentUser {
    if (!response || response.isSuccessful === false || !response.data) {
      this.clearSession();
      throw new Error('Unable to load current user.');
    }

    const currentUser = this.normalizeUser(response.data);
    if (!currentUser || currentUser.role !== Role.Admin) {
      this.clearSession();
      throw new Error('Unable to load current user.');
    }

    return currentUser;
  }

  private normalizeUser(user: any): CurrentUser {
    if (!user) {
      return null;
    }

    const roles = Array.isArray(user.roles) ? user.roles : [];
    const role = this.resolveRole(roles, user.role);

    if ([Role.Admin, Role.Client, Role.User].indexOf(role) === -1) {
      return null;
    }

    const normalized = { ...user, role, roles: roles.length ? roles : [role] };
    delete (normalized as any).token;
    return normalized as CurrentUser;
  }

  private resolveRole(roles: any[], fallbackRole: any): Role {
    const normalizedRoles = roles.map(role => this.toRole(role)).filter(role => !!role);

    if (normalizedRoles.indexOf(Role.Admin) !== -1) {
      return Role.Admin;
    }

    return this.toRole(fallbackRole) || normalizedRoles[0];
  }

  private toRole(role: any): Role {
    const value =
      typeof role === 'string'
        ? role
        : role && (role.name || role.Name || role.role || role.Role || role.value || role.Value);

    if (!value) {
      return null;
    }

    switch (value.toString().toLowerCase()) {
      case 'admin':
        return Role.Admin;
      case 'client':
        return Role.Client;
      case 'user':
        return Role.User;
      default:
        return null;
    }
  }

  private getStoredAuth(): AuthSession & Partial<CurrentUser> {
    return this.parseStoredJson('Auth');
  }

  private getStoredCurrentUser() {
    return this.parseStoredJson('currentUser');
  }

  private parseStoredJson(key: string) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  private clearSession() {
    localStorage.removeItem('Auth');
    localStorage.removeItem('currentUser');
  }
}
